import { useState } from 'react';

const RegisterSection = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    childName: '',
    childAge: '',
    childGender: '',
    // 主修課程 - 每週一、三、五
    mainCourseWeek1: '',
    mainCourseWeek2: '',
    mainCourseWeek3: '',
    // 副修課程 - 每週二、四
    electiveCourseWeek1: '',
    electiveCourseWeek2: '',
    electiveCourseWeek3: '',
    // 下午俱樂部 - 每週選2個
    clubWeek1Activity1: '',
    clubWeek1Activity2: '',
    clubWeek2Activity1: '',
    clubWeek2Activity2: '',
    clubWeek3Activity1: '',
    clubWeek3Activity2: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalInfo: '',
    specialNeeds: '',
    agreement: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const mainCourses = [
    '軟式飛盤', '科學實驗', '躲避球', '3D列印', '漆彈生存遊戲',
    '點心烘焙', '競技籃球', '雷射雕刻', '羽毛球'
  ];

  const electiveCourses = [
    '數學課程', '小小廣播', '英文課程', '遙控賽車', '正音課程',
    '遙控飛機', '小小魔術師', '輕黏土拼貼', '游泳課程', '流行街舞'
  ];

  const clubActivities = [
    '圍巾編織', '鑽石貼畫', '樂樂棒球', '疊杯疊疊樂', '兒童油畫',
    '象棋黑白棋', '羽毛球', '競技籃球', '拼豆', '撲克UNO牌', '漆彈生存遊戲'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreement) {
      setSubmitMessage('請先同意條款與細則');
      return;
    }

    if (formData.specialNeeds.length > 500) {
      setSubmitMessage('特殊需求說明不能超過500字');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d4divt1pkd5ovn9u68b0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitMessage('報名表單已成功提交！我們會盡快與您聯繫。');
        setFormData({
          parentName: '',
          parentPhone: '',
          parentEmail: '',
          childName: '',
          childAge: '',
          childGender: '',
          mainCourseWeek1: '',
          mainCourseWeek2: '',
          mainCourseWeek3: '',
          electiveCourseWeek1: '',
          electiveCourseWeek2: '',
          electiveCourseWeek3: '',
          clubWeek1Activity1: '',
          clubWeek1Activity2: '',
          clubWeek2Activity1: '',
          clubWeek2Activity2: '',
          clubWeek3Activity1: '',
          clubWeek3Activity2: '',
          emergencyContact: '',
          emergencyPhone: '',
          medicalInfo: '',
          specialNeeds: '',
          agreement: false
        });
      } else {
        setSubmitMessage('提交失敗，請稍後再試。');
      }
    } catch (error) {
      setSubmitMessage('提交失敗，請檢查網路連接。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="register" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">
            立即報名
          </h3>
          <p className="text-xl text-gray-600 leading-relaxed">
            填寫報名表單，讓您的孩子開始一段難忘的探險之旅
          </p>
          <div className="mt-4 bg-emerald-50 rounded-xl p-4 inline-block">
            <p className="text-emerald-800 font-semibold">
              <i className="ri-calendar-line mr-2"></i>
              寒假營隊時間：115年1月26日至2月13日（共15天，分3週）
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8">
          <form id="camp-registration-form" onSubmit={handleSubmit} data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 家長資訊 */}
              <div className="md:col-span-2">
                <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2 text-emerald-600">
                    <i className="ri-parent-line"></i>
                  </div>
                  家長資訊
                </h4>
              </div>
              
              <div>
                <label htmlFor="parentName" className="block text-sm font-medium text-gray-700 mb-2">
                  家長姓名 *
                </label>
                <input
                  type="text"
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入家長姓名"
                />
              </div>

              <div>
                <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  聯絡電話 *
                </label>
                <input
                  type="tel"
                  id="parentPhone"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入聯絡電話"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  電子信箱 *
                </label>
                <input
                  type="email"
                  id="parentEmail"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入電子信箱"
                />
              </div>

              {/* 孩童資訊 */}
              <div className="md:col-span-2 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2 text-emerald-600">
                    <i className="ri-user-smile-line"></i>
                  </div>
                  孩童資訊
                </h4>
              </div>

              <div>
                <label htmlFor="childName" className="block text-sm font-medium text-gray-700 mb-2">
                  孩童姓名 *
                </label>
                <input
                  type="text"
                  id="childName"
                  name="childName"
                  value={formData.childName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入孩童姓名"
                />
              </div>

              <div>
                <label htmlFor="childAge" className="block text-sm font-medium text-gray-700 mb-2">
                  年齡 *
                </label>
                <input
                  type="number"
                  id="childAge"
                  name="childAge"
                  value={formData.childAge}
                  onChange={handleInputChange}
                  required
                  min="6"
                  max="18"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入年齡"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="childGender" className="block text-sm font-medium text-gray-700 mb-2">
                  性別 *
                </label>
                <div className="relative">
                  <select
                    id="childGender"
                    name="childGender"
                    value={formData.childGender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                  >
                    <option value="">請選擇性別</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* 課程選擇 */}
              <div className="md:col-span-2 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2 text-emerald-600">
                    <i className="ri-book-line"></i>
                  </div>
                  課程選擇
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  營隊共分3週，請為每週選擇課程
                </p>
              </div>

              {/* 第一週課程 */}
              <div className="md:col-span-2 bg-white rounded-lg p-6 border-2 border-emerald-200">
                <h5 className="text-lg font-semibold text-emerald-700 mb-4">第一週（1/26-1/30）</h5>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="mainCourseWeek1" className="block text-sm font-medium text-gray-700 mb-2">
                      上午主修課程（週一、三、五）*
                    </label>
                    <div className="relative">
                      <select
                        id="mainCourseWeek1"
                        name="mainCourseWeek1"
                        value={formData.mainCourseWeek1}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇主修課程</option>
                        {mainCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="electiveCourseWeek1" className="block text-sm font-medium text-gray-700 mb-2">
                      上午副修課程（週二、四）*
                    </label>
                    <div className="relative">
                      <select
                        id="electiveCourseWeek1"
                        name="electiveCourseWeek1"
                        value={formData.electiveCourseWeek1}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇副修課程</option>
                        {electiveCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="clubWeek1Activity1" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 1 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek1Activity1"
                          name="clubWeek1Activity1"
                          value={formData.clubWeek1Activity1}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="clubWeek1Activity2" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 2 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek1Activity2"
                          name="clubWeek1Activity2"
                          value={formData.clubWeek1Activity2}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 第二週課程 */}
              <div className="md:col-span-2 bg-white rounded-lg p-6 border-2 border-emerald-200">
                <h5 className="text-lg font-semibold text-emerald-700 mb-4">第二週（2/2-2/6）</h5>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="mainCourseWeek2" className="block text-sm font-medium text-gray-700 mb-2">
                      上午主修課程（週一、三、五）*
                    </label>
                    <div className="relative">
                      <select
                        id="mainCourseWeek2"
                        name="mainCourseWeek2"
                        value={formData.mainCourseWeek2}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇主修課程</option>
                        {mainCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="electiveCourseWeek2" className="block text-sm font-medium text-gray-700 mb-2">
                      上午副修課程（週二、四）*
                    </label>
                    <div className="relative">
                      <select
                        id="electiveCourseWeek2"
                        name="electiveCourseWeek2"
                        value={formData.electiveCourseWeek2}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇副修課程</option>
                        {electiveCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="clubWeek2Activity1" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 1 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek2Activity1"
                          name="clubWeek2Activity1"
                          value={formData.clubWeek2Activity1}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="clubWeek2Activity2" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 2 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek2Activity2"
                          name="clubWeek2Activity2"
                          value={formData.clubWeek2Activity2}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 第三週課程 */}
              <div className="md:col-span-2 bg-white rounded-lg p-6 border-2 border-emerald-200">
                <h5 className="text-lg font-semibold text-emerald-700 mb-4">第三週（2/9-2/13）</h5>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="mainCourseWeek3" className="block text-sm font-medium text-gray-700 mb-2">
                      上午主修課程（週一、三、五）*
                    </label>
                    <div className="relative">
                      <select
                        id="mainCourseWeek3"
                        name="mainCourseWeek3"
                        value={formData.mainCourseWeek3}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇主修課程</option>
                        {mainCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="electiveCourseWeek3" className="block text-sm font-medium text-gray-700 mb-2">
                      上午副修課程（週二、四）*
                    </label>
                    <div className="relative">
                      <select
                        id="electiveCourseWeek3"
                        name="electiveCourseWeek3"
                        value={formData.electiveCourseWeek3}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                      >
                        <option value="">請選擇副修課程</option>
                        {electiveCourses.map(course => (
                          <option key={course} value={course}>{course}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="clubWeek3Activity1" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 1 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek3Activity1"
                          name="clubWeek3Activity1"
                          value={formData.clubWeek3Activity1}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="clubWeek3Activity2" className="block text-sm font-medium text-gray-700 mb-2">
                        下午俱樂部課程 2 *
                      </label>
                      <div className="relative">
                        <select
                          id="clubWeek3Activity2"
                          name="clubWeek3Activity2"
                          value={formData.clubWeek3Activity2}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8 appearance-none"
                        >
                          <option value="">請選擇課程</option>
                          {clubActivities.map(activity => (
                            <option key={activity} value={activity}>{activity}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                            <i className="ri-arrow-down-s-line"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 緊急聯絡人 */}
              <div className="md:col-span-2 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2 text-emerald-600">
                    <i className="ri-phone-line"></i>
                  </div>
                  緊急聯絡人
                </h4>
              </div>

              <div>
                <label htmlFor="emergencyContact" className="block text-sm font-medium text-gray-700 mb-2">
                  緊急聯絡人姓名 *
                </label>
                <input
                  type="text"
                  id="emergencyContact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入緊急聯絡人姓名"
                />
              </div>

              <div>
                <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  緊急聯絡電話 *
                </label>
                <input
                  type="tel"
                  id="emergencyPhone"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="請輸入緊急聯絡電話"
                />
              </div>

              {/* 健康資訊 */}
              <div className="md:col-span-2 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2 text-emerald-600">
                    <i className="ri-heart-pulse-line"></i>
                  </div>
                  健康資訊
                </h4>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="medicalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  過敏史或疾病史
                </label>
                <input
                  type="text"
                  id="medicalInfo"
                  name="medicalInfo"
                  value={formData.medicalInfo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder="如有過敏或疾病史請詳細說明，無則填寫「無」"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="specialNeeds" className="block text-sm font-medium text-gray-700 mb-2">
                  特殊需求或備註 (限500字)
                </label>
                <textarea
                  id="specialNeeds"
                  name="specialNeeds"
                  value={formData.specialNeeds}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  placeholder="如有特殊需求或其他需要我們注意的事項，請在此說明"
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.specialNeeds.length}/500
                </div>
              </div>

              {/* 同意條款 */}
              <div className="md:col-span-2 mt-6">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="agreement"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={handleInputChange}
                    required
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="agreement" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    我已詳細閱讀並同意營隊的
                    <a href="#" className="text-emerald-600 hover:text-emerald-700 cursor-pointer">活動條款</a>
                    、
                    <a href="#" className="text-emerald-600 hover:text-emerald-700 cursor-pointer">安全規範</a>
                    及
                    <a href="#" className="text-emerald-600 hover:text-emerald-700 cursor-pointer">個人資料使用政策</a>
                    ，並授權營隊在活動期間拍攝照片用於宣傳用途。
                  </label>
                </div>
              </div>
            </div>

            {/* 提交按鈕 */}
            <div className="mt-8 text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-emerald-600 text-white px-12 py-4 rounded-full text-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '提交中...' : '提交報名表單'}
              </button>
            </div>

            {/* 提交結果訊息 */}
            {submitMessage && (
              <div className={`mt-4 p-4 rounded-lg text-center ${
                submitMessage.includes('成功') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default RegisterSection;